import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { Button, Grid2, Stack, Tab, Tabs, Typography } from "@mui/material";
import { IconEdit, IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { MRT_ColumnDef } from "material-react-table";

import {
  formatCpfCnpj,
  formatDate,
  formatPaymentStatus,
  formatPhone,
  formatPrice,
} from "../helpers/format";

import { listStoreOrders } from "../services/products";

import ContainerLoading from "../components/ContainerLoading";
import PageHeader from "../components/PageHeader";
import PartnersStore from "../components/PartnersStore";
import VirtualizedTable from "../components/VirtualizeTable";
import OrderStatusTag from "../components/OrderStatusTag";
import IndicatorsCard from "../components/IndicatorsCard";
import { customerList } from "../services/customer";
import ModalConfirm from "../components/ModalConfirm";

const Orders = () => {
  const navigate = useNavigate();
  const [openPartnersStore, setOpenPartnersStore] = useState(false);
  const [value, setValue] = useState("1");

  const [open, setOpen] = useState({ status: true, id: null, type: "" });

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    setValue(newValue);
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["listStoreOrders"],
    queryFn: async () => await listStoreOrders(),
    initialData: { orders: [] },
  });

  const { data: customers } = useQuery({
    queryKey: ["customerList"],
    queryFn: async () => await customerList(),
    initialData: [],
  });

  const { orders } = data;

  const loading = isLoading || isFetching;

  const columns = useMemo<MRT_ColumnDef<(typeof orders)[0]>[]>(() => {
    return [
      {
        accessorKey: "status",
        header: "Status",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        size: 110,
        Cell: ({ cell }) => {
          const status = cell.getValue() as string;
          return <OrderStatusTag status={status} />;
        },
        exportValue: (row: any) => formatPaymentStatus(row.status), // ou: "amount"
      },
      {
        accessorKey: "id",
        header: "Pedido",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        size: 80,

        Cell: ({ row }) => {
          return `PEDIDO-${row.original.id}`;
        },
      },
      {
        accessorKey: "customer",
        header: "Cliente",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        Cell: ({ row }) => {
          return row.original?.customer?.name || "-";
        },
        exportValue: "customer.name", // 👈 usamos isso na exportação
      },
      {
        accessorKey: "amount",
        header: "Valor",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        Cell: ({ row }) => {
          return formatPrice(row.original?.amount) || "-";
        },
        exportValue: (row: any) => formatPrice(row.amount), // ou: "amount"
      },
      {
        accessorKey: "createdAt",
        header: "Criado em",
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        size: 80,
        Cell: ({ row }) => {
          return formatDate(row.original?.createdAt) || "-";
        },
        exportValue: (row: any) => String(formatDate(row.createdAt)),
      },
      {
        id: "detalhes",
        header: "Detalhes",
        size: 80,
        Cell: ({ row }) => {
          return (
            <IconEye
              onClick={() => navigate(`/orders/${row.original.id}/details`)}
              style={{ cursor: "pointer", color: "#FFF" }}
            />
          );
        },
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
      },
    ];
  }, [orders]);

  const columnsCustomers = useMemo<MRT_ColumnDef<(typeof orders)[0]>[]>(() => {
    return [
      {
        accessorKey: "name",
        header: "Cliente",
      },
      {
        accessorKey: "document",
        header: "Documento",
        Cell: ({ row }) => {
          return formatCpfCnpj(row.original?.document) || "-";
        },
        exportValue: (row: any) => formatCpfCnpj(row.document), // ou: "amount"
      },
      {
        accessorKey: "phone",
        header: "Telefone",
        Cell: ({ row }) => {
          return formatPhone(row.original?.phone) || "-";
        },
        exportValue: (row: any) => formatPhone(row.phone),
      },

      {
        id: "edit",
        header: "Editar",
        size: 70,
        Cell: ({ row }: any) => (
          <IconEdit
            onClick={() => {
              setOpen({ status: true, id: row?.original?.id, type: "EDIT" });
            }}
            style={{ cursor: "pointer", color: "#FFF" }}
          />
        ),
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
      },
      {
        id: "delete",
        header: "Excluir",
        size: 70,
        Cell: ({ row }: any) => (
          <IconTrash
            onClick={() => {
              setOpen({ status: true, id: row?.original?.id, type: "DELETE" });
            }}
            style={{ cursor: "pointer", color: "#d32f2f" }}
          />
        ),
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
      },
    ];
  }, [customers]);

  const onClose = () => {
    setOpen({ status: true, id: null, type: "" });
  };

  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Pedidos"
          subtitle="Informações gerais dos pedidos."
          options={
            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                sx={{ gap: 0.5 }}
                onClick={() => navigate("/orders/register")}
              >
                <IconPlus size={18} />
                <Typography variant="body2">Novo pedido</Typography>
              </Button>
            </Stack>
          }
        />
        <Grid2 size={12}>
          <IndicatorsCard
            data={[
              {
                id: 1,
                title: "Pedidos pagos",
                value: data?.ordersPaid,
                icon: "icon_school",
                size: { xs: 12, sm: 6, md: 4 },
              },
              {
                id: 2,
                title: "Faturamento",
                value: formatPrice(data?.ordersRevenue),
                icon: "icon_school",
                size: { xs: 12, sm: 6, md: 4 },
              },

              {
                id: 3,
                title: "Faturamento  ( mês )",
                value: formatPrice(data?.ordersRevenueMonth),
                icon: "icon_school",
                size: { xs: 12, sm: 6, md: 4 },
              },
            ]}
          />
        </Grid2>
        <Grid2 size={12}>
          <Tabs
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              marginBottom: 2,
            }}
            onChange={handleChangeValue}
            value={value}
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Pedidos" value="1" />
            {/* <Tab label="Clientes" value="2" /> */}
          </Tabs>
        </Grid2>
        {value === "1" && (
          <Grid2 size={12}>
            <VirtualizedTable
              enableExportCSV
              data={orders}
              columns={columns}
              fetchNextPage={() => Promise.resolve()}
              isLoading={false}
            />
          </Grid2>
        )}
        {value === "2" && (
          <Grid2 size={12}>
            <VirtualizedTable
              enableExportCSV
              data={customers}
              columns={columnsCustomers}
              fetchNextPage={() => Promise.resolve()}
              isLoading={false}
            />
          </Grid2>
        )}
      </Grid2>

      {open.type === "DELETE" && (
        <ModalConfirm
          title="Excluir cliente"
          description="Tem certeza de que deseja excluir esse cliente?"
          onClose={onClose}
          onAction={() => {}}
          open={open.status}
          buttonColor="error"
          buttonTitle="Excluir cliente"
        />
      )}
      <PartnersStore
        open={openPartnersStore}
        onClose={() => setOpenPartnersStore(false)}
      />
    </ContainerLoading>
  );
};

export default Orders;
